'use client';

/**
 * Check feasibility from any DANI conversation (2026-10-15). A behavior
 * analysis ("who visited this store"), a pasted brief or a thread copied from
 * another tool is already the audience: one press sends DANI the feasibility
 * rules, once per conversation, and asks it to build the check from what the
 * conversation says, asking only what would change the number. The check then
 * runs in the chat and the feasibility card takes this button's place.
 *
 * Files go through the composer's own paperclip and drag-and-drop: in a
 * feasibility chat they are read here (`useFeasibilityFiles`), so the chat has
 * one upload, not two (2026-10-27).
 *
 * Internal only, behind the feasibility-dani flag; the routes check isInternal.
 */
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { feasibilityApi } from '@/@feasibility-dani/api';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { useStudioAI } from '@/@studio-ai/context';
import {
    BRIEF_FILE_TYPES,
    MAX_BRIEF_FILES,
    type BriefFile,
} from '@/lib/feasibility/contracts/brief';
import { hasFeasibilityRules, launchPrompt } from '@/lib/feasibility/dani/primer-marks';

import { saveListsIn } from '../files/upload';
import { asksFeasibility, briefFile } from './feasibility-routing';
import { fileBriefForDani, writeFileDrafts } from './file-drafts';

type ChatMessageLike = { role?: unknown; content: unknown; silent?: boolean };

function textOf(content: unknown): string {
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return '';
    return content
        .map((b) => (b && typeof b === 'object' && 'text' in b ? String(b.text ?? '') : ''))
        .join(' ');
}

/** Rules a conversation needs before its first feasibility message, or null when it has them. */
async function rulesIfNeeded(messages: ChatMessageLike[]): Promise<string | null> {
    if (hasFeasibilityRules(messages)) return null;
    const primer = await feasibilityApi.strategyPrimer();
    return primer.messages
        .filter((m) => m.role === 'user')
        .map((m) => textOf(m.content))
        .join('\n\n');
}

/**
 * A brief, an email, a questionnaire or a location or panelist list, from the
 * composer's paperclip: read by the Feasibility page's reader, then handed to
 * DANI as drafts it can run. Several files go together (a brief and its station
 * workbook), up to the reader's limit.
 */
export function useFeasibilityFiles(conversationId: string, messages: ChatMessageLike[]) {
    const { sendSilentMessage } = useStudioAI();
    const [reading, setReading] = useState(false);

    const readFiles = useCallback(
        async (picked: File[]) => {
            if (!picked.length) return;
            setReading(true);
            try {
                const chosen = picked.slice(0, MAX_BRIEF_FILES);
                if (picked.length > MAX_BRIEF_FILES)
                    toast.info(`Reading the first ${MAX_BRIEF_FILES} files.`);
                const read = await Promise.all(chosen.map(briefFile));
                const problems = read.filter((x): x is string => typeof x === 'string');
                const files = read.filter((x): x is BriefFile => typeof x !== 'string');
                if (problems.length) toast.error(problems.join(' '));
                if (!files.length) return;
                toast.info('Reading the file for the feasibility check…', { autoClose: 4000 });
                const result = await feasibilityApi.brief({ files });
                // The lists among the files (stations, zips, IDs) are kept in Files too.
                void saveListsIn(chosen).catch(() => undefined);
                const names = files.map((f) => f.name);
                writeFileDrafts(conversationId, {
                    briefId: result.briefId,
                    files: names,
                    drafts: result.drafts,
                });
                await sendSilentMessage({
                    prompt: fileBriefForDani(result, names, await rulesIfNeeded(messages)),
                    conversationId,
                    parentMessageId: '0',
                });
            } catch (err) {
                toast.error(
                    `The file could not be read for feasibility: ${err instanceof Error ? err.message : 'unknown error'}.`,
                );
            } finally {
                setReading(false);
            }
        },
        [conversationId, messages, sendSilentMessage],
    );

    return { readFiles, reading, accept: BRIEF_FILE_TYPES };
}

export function FeasibilityLaunch({
    conversationId,
    messages,
    hidden = false,
}: {
    conversationId: string;
    messages: ChatMessageLike[];
    /** A check is already in view: its card takes this button's place. */
    hidden?: boolean;
}) {
    const { hasFlag } = useFeatureFlags();
    const { sendSilentMessage, isLoading } = useStudioAI();
    const [busy, setBusy] = useState(false);

    // While DANI is still answering, its reply may be the next step itself (UAT F55).
    if (hidden || isLoading || !hasFlag('feasibility-dani') || messages.length === 0) return null;

    const lastAsk = [...messages].reverse().find((m) => m.role === 'user' && !m.silent);
    const asked = lastAsk ? asksFeasibility(textOf(lastAsk.content)) : false;

    const launch = async () => {
        setBusy(true);
        try {
            const rules = await rulesIfNeeded(messages);
            await sendSilentMessage({
                prompt: launchPrompt(rules),
                conversationId,
                parentMessageId: '0',
            });
        } catch (err) {
            toast.error(
                `Feasibility could not start: ${err instanceof Error ? err.message : 'unknown error'}. Try again.`,
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <Button
                size="sm"
                radius="full"
                variant={asked ? 'solid' : 'bordered'}
                color={asked ? 'secondary' : 'default'}
                isLoading={busy}
                isDisabled={isLoading}
                startContent={busy ? null : <Icon icon="lucide:target" className="h-4 w-4" />}
                onPress={() => void launch()}
            >
                {asked ? 'Check feasibility for this audience' : 'Check feasibility'}
            </Button>
            {asked && (
                <span className="text-tiny text-default-500">
                    DANI builds the check from this conversation and runs it here.
                </span>
            )}
        </div>
    );
}
